import { map, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import CartCount from 'components/CartCount';
import { AllbirdsLogo } from 'components/Icon';
import IconButton from 'components/IconButton';
import SearchIcon from 'components/SearchIcon';
import { POSITIONS } from 'constants/topNavigation';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import TopNavigationHeaderDesktopButton from './TopNavigationHeaderDesktopButton';
import TopNavigationHeaderDesktopIcon from './TopNavigationHeaderDesktopIcon';
import TopNavigationHeaderDesktopLink from './TopNavigationHeaderDesktopLink';

const LOGO_WIDTH = 100;
const HEADER_HEIGHT = 60;

const TopNavigationHeaderDesktop = ({
  l1Options,
  setCurrentTopNavigationNode,
  closeTopNavigation,
  trackNavigationRerunRenamingExperimentEngagement,
  trackNavigation,
  trackNavigationClick,
  navigationRerunRenamingExperimentAnalyticsData,
  isTopNavigationOpen,
  itemCount,
  openCart,
  isCartOpen,
  showSearch,
}) => (
  <nav className="TopNavigationHeaderDesktop">
    <ul className="TopNavigationHeaderDesktop__list TopNavigationHeaderDesktop__list--left">
      {map(
        l1Options,
        l1Option =>
          l1Option.position === POSITIONS.PRIMARY && (
            <li
              className="TopNavigationHeaderDesktop__option TopNavigationHeaderDesktop__option--primary"
              key={l1Option.id}
            >
              {isEmpty(l1Option.subNavigations) && l1Option.linkUrl ? (
                <TopNavigationHeaderDesktopLink
                  url={l1Option.linkUrl}
                  label={l1Option.label}
                  isSalesColorRed={l1Option.isSalesColorRed}
                  trackNavigation={trackNavigation}
                  trackNavigationClick={trackNavigationClick}
                />
              ) : (
                <TopNavigationHeaderDesktopButton
                  l1Option={l1Option}
                  closeTopNavigation={closeTopNavigation}
                  setCurrentTopNavigationNode={setCurrentTopNavigationNode}
                  isTopNavigationOpen={isTopNavigationOpen}
                  trackNavigationClick={trackNavigationClick}
                />
              )}
            </li>
          )
      )}
    </ul>

    <div className="TopNavigationHeaderDesktop__logo">
      <a
        className="TopNavigationHeaderDesktop__logo-anchor"
        href="/"
        onClick={() => trackNavigation('/')}
        aria-label={I18n.localize('topNavigation.allbirdsHome')}
      >
        <AllbirdsLogo />
      </a>
    </div>

    <ul className="TopNavigationHeaderDesktop__list TopNavigationHeaderDesktop__list--right">
      {map(
        l1Options,
        l1Option =>
          l1Option.position === POSITIONS.SECONDARY && (
            <li
              className="TopNavigationHeaderDesktop__option TopNavigationHeaderDesktop__option--secondary"
              key={l1Option.id}
            >
              {isEmpty(l1Option.subNavigations) && l1Option.linkUrl ? (
                <TopNavigationHeaderDesktopLink
                  url={l1Option.linkUrl}
                  label={l1Option.label}
                  isSalesColorRed={l1Option.isSalesColorRed}
                  trackNavigationRerunRenamingExperimentEngagement={
                    trackNavigationRerunRenamingExperimentEngagement
                  }
                  navigationRerunRenamingExperimentAnalyticsData={
                    navigationRerunRenamingExperimentAnalyticsData
                  }
                  trackNavigation={trackNavigation}
                  trackNavigationClick={trackNavigationClick}
                />
              ) : (
                <TopNavigationHeaderDesktopButton
                  l1Option={l1Option}
                  closeTopNavigation={closeTopNavigation}
                  setCurrentTopNavigationNode={setCurrentTopNavigationNode}
                  isTopNavigationOpen={isTopNavigationOpen}
                  trackNavigationClick={trackNavigationClick}
                />
              )}
            </li>
          )
      )}

      {showSearch && (
        <li className="TopNavigationHeaderDesktop__option TopNavigationHeaderDesktop__option--tertiary TopNavigationHeaderDesktop__option--search">
          <SearchIcon />
        </li>
      )}

      {map(
        l1Options,
        l1Option =>
          l1Option.position === POSITIONS.TERTIARY && (
            <li
              className="TopNavigationHeaderDesktop__option TopNavigationHeaderDesktop__option--tertiary"
              key={l1Option.id}
            >
              <TopNavigationHeaderDesktopIcon
                l1Option={l1Option}
                closeTopNavigation={closeTopNavigation}
                setCurrentTopNavigationNode={setCurrentTopNavigationNode}
                trackNavigation={trackNavigation}
                trackNavigationClick={trackNavigationClick}
                isTopNavigationOpen={isTopNavigationOpen}
              />
            </li>
          )
      )}

      <li>
        <IconButton
          onClick={openCart}
          aria-expanded={isCartOpen}
          aria-controls="cart"
          aria-label={I18n.localize('topNavigation.toggleCart')}
          className="TopNavigationHeaderDesktop__cart-icon-button"
        >
          <CartCount itemCount={itemCount} />
        </IconButton>
      </li>
    </ul>

    <style jsx>{`
      .TopNavigationHeaderDesktop {
        background-color: ${Theme.get('colors-white')};
        width: 100%;
        height: ${HEADER_HEIGHT}px;
        padding-left: 0px;
        position: relative;
        margin: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 2px 24px 0 rgba(0, 0, 0, 0.09);
        z-index: 1; /* Added so dropshadow is above drawer below */
      }

      .TopNavigationHeaderDesktop__list {
        list-style: none;
        display: flex;
        align-items: center;
        flex: 1;
      }

      ul.TopNavigationHeaderDesktop__list,
      .TopNavigationHeaderDesktop__list li {
        margin-left: 0;
        padding-left: 0;
      }

      .TopNavigationHeaderDesktop__list--left {
        justify-content: flex-start;
      }

      .TopNavigationHeaderDesktop__list--right {
        justify-content: flex-end;
      }

      .TopNavigationHeaderDesktop__logo {
        justify-content: flex-start;
        width: ${LOGO_WIDTH}px;
      }

      .TopNavigationHeaderDesktop__logo-anchor {
        display: block;
        width: 100%;
      }

      .TopNavigationHeaderDesktop__cart {
        display: table;
      }
    `}</style>
    <style jsx global>{`
      .TopNavigationHeaderDesktop__cart-icon-button {
        align-items: center;
        display: table-cell;
        margin: 0 ${Theme.get('spacing') * 5}px 0 ${Theme.get('spacing') * 2}px;
      }

      .TopNavigationHeaderDesktop__list--right .TopNavigationHeaderDesktop__button-content {
        margin: 0;
      }

      .TopNavigationHeaderDesktop__list--right .TopNavigationHeaderDesktop__button {
        margin-left: ${Theme.get('spacing') * 5}px;
      }
      .TopNavigationHeaderDesktop__option--search .Input.SearchBarMinimal__input {
        height: ${Theme.get('spacing') * 6}px;
        font-size: ${Theme.get('font-size-description-px')};
      }

      ul.TopNavigationHeaderDesktop__list li.TopNavigationHeaderDesktop__option--search {
        min-height: 42px;
        min-width: 42px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
    `}</style>
  </nav>
);

TopNavigationHeaderDesktop.propTypes = {
  l1Options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      position: PropTypes.oneOf(Object.values(POSITIONS)).isRequired,
      subNavigations: PropTypes.arrayOf(PropTypes.shape({})),
      id: PropTypes.string,
    })
  ).isRequired,
  setCurrentTopNavigationNode: PropTypes.func.isRequired,
  closeTopNavigation: PropTypes.func.isRequired,
  trackNavigationRerunRenamingExperimentEngagement: PropTypes.func,
  trackNavigation: PropTypes.func.isRequired,
  isTopNavigationOpen: PropTypes.bool.isRequired,
  itemCount: PropTypes.number.isRequired,
  openCart: PropTypes.func.isRequired,
  isCartOpen: PropTypes.bool.isRequired,
  showSearch: PropTypes.bool.isRequired,
  trackNavigationClick: PropTypes.func,
  navigationRerunRenamingExperimentAnalyticsData: PropTypes.shape({
    dyDecisionId: PropTypes.string,
    dyVariationId: PropTypes.number,
  }),
};

TopNavigationHeaderDesktop.defaultProps = {
  trackNavigationRerunRenamingExperimentEngagement: () => {},
  trackNavigationClick: () => {},
  navigationRerunRenamingExperimentAnalyticsData: null,
};

export default TopNavigationHeaderDesktop;
